import { createRouter, createWebHistory } from "vue-router";

import Login from "@/views/login/Login.vue";
import ChangePassword from "@/views/changePassword/ChangePassword.vue";
import SetPassword from "@/views/setPassword/SetPassword.vue";
import ResetPassword from "@/views/resetPassword/ResetPassword.vue";

import Dashboard from "@/views/dashboard/Dashboard.vue";
import Chat from "@/views/chats/Chat.vue";
import Dispatchers from "@/views/dispatchers/view/Dispatchers.vue";

import Facilities from "@/views/companies/facilities/view/Facilities.vue";
import FacilitiesEdit from "@/views/companies/facilities/edit/Edit.vue";

import Coordinators from "@/views/coordinators/Coordinators.vue";
import Owners from "@/views/owners/Owners.vue";
import Drivers from "@/views/drivers/Drivers.vue";
import UserEdit from "@/views/users/edit/UserEdit.vue";
import UserView from "@/views/users/view/UserView.vue";

import Trucks from "@/views/fleets/Trucks.vue";
import TruckView from "@/views/fleets/view/TruckView.vue";
import TrucksSearch from "@/views/fleets/components/TrucksSearch.vue";
import TruckEdit from "@/views/fleets/edit/TruckEdit.vue";
import Logs from "@/views/logs/Logs.vue";
import TrucksHistory from "@/views/fleets/history/TrucksHistory.vue";
import Expirations from "@/views/fleets/expirations/Expirations.vue";
import MapPosition from "@/views/map/MapPosition.vue";
import Profile from "@/views/profile/Profile.vue";
import Orders from "@/views/orders/Orders.vue";
import OrderEdit from "@/views/orders/edit/OrderEdit.vue";
import TripEdit from "@/views/trips/edit/TripEdit.vue";
import CompanyProfile from "@/views/companies/profile/CompanyProfile.vue";
import Carriers from "@/views/companies/carriers/view/Carriers.vue";
import CarriersEdit from "@/views/companies/carriers/edit/Edit.vue";
import Customers from "@/views/companies/customers/view/Customers.vue";
import CustomerEdit from "@/views/companies/customers/edit/Edit.vue";
import { AuthorizedGuard } from "./guards/authorized.guard";
import NotFound from "@/views/notFound/NotFound.vue";
import OrderView from "@/views/orders/view/OrderView.vue";
import TripView from "@/views/trips/view/TripView.vue";
import TripOverviewTab from "@/views/trips/view/components/TripOverviewTab.vue";
import TripCheckCallsTab from "@/views/trips/view/components/TripCheckCallsTab.vue";
import TripCheckCallsRouteTab from "@/views/trips/view/components/TripCheckCallsRouteTab.vue";
import TripBrokerUpdatesTab from "@/views/trips/view/components/TripBrokerUpdatesTab.vue";
import TripBrokerUpdatesOrderTab from "@/views/trips/view/components/TripBrokerUpdatesOrderTab.vue";
import TripMonitor from "@/views/trips/monitor/TripMonitor.vue";
import Reports from "@/views/reports/Reports.vue";
import Invoices from "@/views/billing/invoices/Invoices.vue";
import DispatcherEdit from "@/views/dispatchers/edit/DispatcherEdit.vue";
import DispatcherCreate from "@/views/dispatchers/create/DispatcherCreate.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";
import TrucksMap from "@/views/fleets/map/TrucksMap.vue";

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", redirect: "/dashboard", name: "index" },

    /// Security
    {
      path: "/login",
      component: Login,
      name: "login",
      meta: { layout: "blank" },
    },
    {
      path: "/change-password",
      component: ChangePassword,
      name: "change-password",
    },
    {
      path: "/reset-password",
      component: ResetPassword,
      name: "reset-password",
      meta: { layout: "blank" },
    },
    {
      path: "/dispatchers/:id/set-password",
      component: SetPassword,
      name: "set-password",
      meta: { layout: "blank" },
    },

    /// Dashboard
    {
      path: "/dashboard",
      component: Dashboard,
      name: "dashboard",
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/account/profile",
      component: Profile,
      name: "account-profile",
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/chat",
      component: Chat,
      name: "chat",
      meta: { permission: DispatcherPermissionValue.ChatList },
      beforeEnter: [AuthorizedGuard],
    },

    /// Orders
    {
      path: "/orders",
      beforeEnter: [AuthorizedGuard],
      children: [
        {
          path: "",
          component: Orders,
          name: "orders",
          meta: { permission: DispatcherPermissionValue.OrdersList },
        },
        {
          path: "create",
          component: OrderEdit,
          name: "order-create",
          meta: { permission: DispatcherPermissionValue.OrdersEdit },
        },
        {
          path: ":id/edit",
          component: OrderEdit,
          name: "order-edit",
          meta: { permission: DispatcherPermissionValue.OrdersEdit },
        },
        {
          path: ":id",
          component: OrderView,
          name: "order-view",
          meta: { permission: DispatcherPermissionValue.OrdersInfo },
        },
      ],
    },

    /// Trips
    {
      path: "/trips",
      beforeEnter: [AuthorizedGuard],
      children: [
        {
          path: "monitor",
          component: TripMonitor,
          name: "trip-monitor",
          meta: { permission: DispatcherPermissionValue.TripMonitorList },
        },
        {
          path: ":id",
          component: TripEdit,
          name: "trip-edit",
          meta: { permission: DispatcherPermissionValue.TripsEdit },
        },
        {
          path: ":id/view",
          component: TripView,
          name: "trip-view-base",
          meta: { permission: DispatcherPermissionValue.TripsInfo },
          children: [
            {
              path: "overview",
              name: "trip-view",
              component: TripOverviewTab,
              strict: false,
            },
            {
              path: "check-calls",
              name: "trip-check-calls",
              component: TripCheckCallsTab,
              meta: { permission: DispatcherPermissionValue.CheckCallsList },
              children: [
                {
                  path: "routes/:routeId",
                  name: "trip-check-calls-route",
                  component: TripCheckCallsRouteTab,
                  meta: { permission: DispatcherPermissionValue.CheckCallsList },
                },
              ],
            },
            {
              path: "broker-updates",
              name: "trip-broker-updates",
              component: TripBrokerUpdatesTab,
              meta: { permission: DispatcherPermissionValue.BrokerUpdatesList },
              children: [
                {
                  path: "all",
                  name: "trip-broker-updates-all",
                  component: TripBrokerUpdatesOrderTab,
                  meta: { permission: DispatcherPermissionValue.BrokerUpdatesList },
                },
                {
                  path: "order/:orderId",
                  name: "trip-broker-updates-order",
                  component: TripBrokerUpdatesOrderTab,
                  meta: { permission: DispatcherPermissionValue.BrokerUpdatesList },
                },
              ],
            },
          ],
        },
      ],
    },

    /// Users
    {
      path: "/users",
      beforeEnter: [AuthorizedGuard],
      children: [
        {
          path: "drivers",
          component: Drivers,
          name: "drivers",
          meta: { permission: DispatcherPermissionValue.UsersList },
        },
        {
          path: "owners",
          component: Owners,
          name: "owners",
          meta: { permission: DispatcherPermissionValue.UsersList },
        },
        {
          path: "coordinators",
          component: Coordinators,
          name: "coordinators",
          meta: { permission: DispatcherPermissionValue.UsersList },
        },
        {
          path: "create",
          component: UserEdit,
          name: "user-create",
          meta: { permission: DispatcherPermissionValue.UsersEdit },
        },
        {
          path: ":id(\\d+)/edit",
          component: UserEdit,
          name: "user-edit",
          meta: { permission: DispatcherPermissionValue.UsersEdit },
        },
        {
          path: ":id(\\d+)/view",
          component: UserView,
          name: "user-view",
          meta: { permission: DispatcherPermissionValue.UsersInfo },
        },
      ],
    },

    /// Company
    {
      path: "/company",
      beforeEnter: [AuthorizedGuard],
      children: [
        {
          path: "profile",
          component: CompanyProfile,
          name: "company-profile",
          meta: { permission: DispatcherPermissionValue.CompaniesEdit },
        },
        {
          path: "carriers",
          component: Carriers,
          name: "carriers",
          meta: { permission: DispatcherPermissionValue.CompaniesList },
        },
        {
          path: "carriers/:id/edit",
          component: CarriersEdit,
          name: "carriers-edit",
          meta: { permission: DispatcherPermissionValue.CompaniesEdit },
        },
        {
          path: "customers",
          component: Customers,
          name: "customers",
          meta: { permission: DispatcherPermissionValue.CompaniesList },
        },
        {
          path: "customers/:id/edit",
          component: CustomerEdit,
          name: "customer-edit",
          meta: { permission: DispatcherPermissionValue.CompaniesEdit },
        },
        {
          path: "facilities",
          component: Facilities,
          name: "facilities",
          meta: { permission: DispatcherPermissionValue.CompaniesList },
        },
        {
          path: "facilities/create",
          component: FacilitiesEdit,
          name: "facilities-create",
          meta: { permission: DispatcherPermissionValue.CompaniesEdit },
        },
        {
          path: "facilities/:id/edit",
          component: FacilitiesEdit,
          name: "facilities-edit",
          meta: { permission: DispatcherPermissionValue.CompaniesEdit },
        },
      ],
    },

    // Fleets
    {
      path: "/fleets",
      beforeEnter: [AuthorizedGuard],
      children: [
        {
          path: "trucks",
          component: Trucks,
          name: "trucks",
          meta: { permission: DispatcherPermissionValue.TrucksList },
        },
        {
          path: "history",
          component: TrucksHistory,
          name: "trucks-history",
          meta: { permission: DispatcherPermissionValue.TrucksList },
        },
        {
          path: "search",
          component: TrucksSearch,
          name: "trucks-search",
          meta: { permission: DispatcherPermissionValue.TrucksSearch },
        },
        {
          path: "expirations",
          component: Expirations,
          name: "trucks-expirations",
          meta: { permission: DispatcherPermissionValue.TrucksDocumentExpirationReport },
        },
        {
          path: "trucks/:id",
          component: TruckView,
          name: "truck-view",
          meta: { permission: DispatcherPermissionValue.TrucksInfo },
        },
        {
          path: "trucks/:id/edit",
          component: TruckEdit,
          name: "truck-edit",
          meta: { permission: DispatcherPermissionValue.TrucksEdit },
        },
        {
          path: "map",
          component: TrucksMap,
          name: "trucks-map",
          meta: { permission: DispatcherPermissionValue.MapShowTrucks },
        },
      ],
    },

    /// Dispatchers
    {
      path: "/dispatchers",
      component: Dispatchers,
      name: "dispatchers",
      meta: { permission: DispatcherPermissionValue.DispatchersList },
      beforeEnter: [AuthorizedGuard],
    },
    {
      path: "/dispatcher",
      beforeEnter: [AuthorizedGuard],
      meta: { permission: DispatcherPermissionValue.DispatchersEdit },
      children: [
        {
          path: "create",
          component: DispatcherCreate,
          name: "dispatcher-create",
        },
        {
          path: "edit/:id",
          component: DispatcherEdit,
          name: "dispatcher-edit",
        },
      ],
    },

    {
      path: "/logs",
      component: Logs,
      name: "logs",
      meta: { permission: DispatcherPermissionValue.DispatchersLogs },
      beforeEnter: [AuthorizedGuard],
    },

    /// Service
    {
      path: "/map/position",
      component: MapPosition,
      name: "map-position",
      beforeEnter: [AuthorizedGuard],
      meta: { layout: "blank" },
    },

    /// Billing
    {
      path: "/billing",
      beforeEnter: [AuthorizedGuard],
      children: [
        {
          path: "invoices",
          component: Invoices,
          name: "invoices",
        },
      ],
    },

    // Reports
    {
      path: "/reports",
      component: Reports,
      name: "reports",
      meta: { permission: DispatcherPermissionValue.ReportsList },
      beforeEnter: [AuthorizedGuard],
    },

    // Fallback
    { path: "/:pathMatch(.*)*", name: "not-found", component: NotFound },
  ],
});
